import { useState } from "react";

import {
  faClock,
  faEye,
  faPencil,
  faPlus,
  faRocket,
  faShieldAlt,
  faTrash,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import { find } from "lodash-es";

import { AddUserToRoleModal } from "src/adminPanel/AddUserToRoleModal";
import {
  useAllOrgs,
  useDeleteRole,
  useUsersByRole,
} from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { RoleInDB, User } from "src/clients/taktile-api";
import { Icon } from "src/design-system/Icon";

const getRoleIcon = (roleName: string) => {
  switch (roleName) {
    case "oncall_group":
      return faEye;
    case "oncall_write":
      return faPencil;
    case "taktile_d_admin":
      return faShieldAlt;
    case "fwd_d":
      return faRocket;
    default:
      return faUsers;
  }
};

const getRoleColor = (roleName: string) => {
  switch (roleName) {
    case "oncall_group":
      return "text-orange-600 bg-orange-50 border-orange-200";
    case "oncall_write":
      return "text-red-600 bg-red-50 border-red-200";
    case "taktile_d_admin":
      return "text-purple-600 bg-purple-50 border-purple-200";
    case "fwd_d":
      return "text-blue-600 bg-blue-50 border-blue-200";
    default:
      return "text-gray-600 bg-gray-50 border-gray-200";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
};

const RoleSection: React.FC<{ roleName: string; title: string }> = ({
  roleName,
  title,
}) => {
  const allOrgs = useAllOrgs();
  const queryResult = useUsersByRole(roleName);
  const deleteRole = useDeleteRole();
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const handleDeleteUserRole =
    (roleId: string, userEmail: string) => async () => {
      if (
        window.confirm(
          `Are you sure you want to remove the ${title} role from ${userEmail}?`,
        )
      ) {
        await deleteRole.mutateAsync(roleId);
      }
    };

  const showOrganization = roleName === "fwd_d";

  // Flatten all pages of users into a single array
  const allUsers = queryResult.data?.pages.flatMap((page) => page.users) ?? [];

  const renderContent = () => {
    if (queryResult.isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading users...</div>
        </div>
      );
    }

    if (queryResult.isError) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">Error loading users</div>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {allUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Icon icon={getRoleIcon(roleName)} size="md" />
            </div>
            <h3 className="mb-2 font-medium text-gray-900 text-lg">
              No users found
            </h3>
            <p className="mb-4 text-gray-500 text-sm">
              No users have been assigned the {title.toLowerCase()} role yet.
            </p>
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={() => setIsAddUserModalOpen(true)}
            >
              Add First User
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  {showOrganization && (
                    <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    Role Assigned
                  </th>
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {allUsers.map((user: User) => (
                  <tr
                    key={user.id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center">
                        {user.avatar_url ? (
                          <img
                            alt={user.email || user.username || "User"}
                            className="h-8 w-8 rounded-full object-cover"
                            src={user.avatar_url}
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                            <span className="font-inter-medium-12px text-gray-600">
                              {user.email?.charAt(0).toUpperCase() ||
                                user.username?.charAt(0).toUpperCase() ||
                                "U"}
                            </span>
                          </div>
                        )}
                        <div className="ml-3">
                          <div className="font-medium text-gray-900 text-sm">
                            {user.full_name ||
                              user.email ||
                              user.username ||
                              "Unknown User"}
                          </div>
                          <div className="font-inter-normal-12px text-gray-500">
                            {user.email && user.email !== user.username
                              ? user.email
                              : `ID: ${user.id}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    {showOrganization && (
                      <td className="whitespace-nowrap px-4 py-3 text-gray-900 text-sm">
                        {user.roles
                          ?.filter((role: RoleInDB) => role.name === roleName)
                          .map((role: RoleInDB) => (
                            <div
                              key={role.id}
                              className="flex items-center gap-2"
                            >
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-inter-medium-12px text-blue-800">
                                {find(allOrgs.data, {
                                  id: role.arguments?.org_id,
                                })?.name || "Unknown Organization"}
                              </span>
                            </div>
                          ))}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 text-sm">
                      {user.roles
                        ?.filter((role: RoleInDB) => role.name === roleName)
                        .map((role: RoleInDB) => (
                          <div
                            key={role.id}
                            className="flex items-center gap-2"
                          >
                            <div className="flex items-center gap-1 font-inter-normal-12px text-gray-500">
                              <Icon icon={faClock} size="xs" />
                              <span>{formatDate(role.created_at)}</span>
                            </div>
                          </div>
                        ))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500 text-sm">
                      {user.roles
                        ?.filter((role: RoleInDB) => role.name === roleName)
                        .map((role: RoleInDB) => (
                          <Button
                            key={role.id}
                            iconLeft={faTrash}
                            size="sm"
                            variant="warning"
                            onClick={handleDeleteUserRole(
                              role.id,
                              user.email || user.username || "this user",
                            )}
                          >
                            Remove
                          </Button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {queryResult.hasNextPage && (
              <div className="flex justify-center border-gray-200 border-t px-4 py-3">
                <Button
                  disabled={queryResult.isFetchingNextPage}
                  size="sm"
                  variant="secondary"
                  onClick={() => queryResult.fetchNextPage()}
                >
                  {queryResult.isFetchingNextPage ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${getRoleColor(roleName)}`}
          >
            <Icon icon={getRoleIcon(roleName)} size="md" />
          </div>
          <div>
            <h4 className="font-bold text-2xl text-gray-900">{title}</h4>
            <p className="text-gray-500 text-sm">
              Manage users with {title.toLowerCase()} permissions
            </p>
          </div>
        </div>
        <Button
          iconLeft={faPlus}
          size="sm"
          variant="primary"
          onClick={() => setIsAddUserModalOpen(true)}
        >
          Add User
        </Button>
      </div>
      {renderContent()}
      <AddUserToRoleModal
        isOpen={isAddUserModalOpen}
        roleName={roleName}
        roleTitle={title}
        onClose={() => setIsAddUserModalOpen(false)}
      />
    </div>
  );
};

export const RoleBasedUsers: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="font-bold text-3xl text-gray-900">Role Management</h1>
          <p className="mt-2 text-gray-600 text-lg">
            Manage user roles and permissions across the platform
          </p>
        </div>

        <div className="space-y-8">
          <RoleSection
            roleName="oncall_group"
            title="On-Call Group Users (includes support heroes)"
          />
          <RoleSection roleName="oncall_write" title="On-Call Write Users" />
          <RoleSection roleName="taktile_d_admin" title="Taktile Admin Users" />
          <RoleSection roleName="fwd_d" title="Forward Deployed" />
        </div>
      </div>
    </div>
  );
};
